import { Grid, Box, Icon, IconButton, Stack, Typography } from "@mui/material";
import AiIcon from "assets/icons/magic.svg";
import Background from "assets/poly_bg.svg";
import ActionButton from "common/components/ActionButton";
import Chips from "common/components/Chips";
import Page from "common/components/Page";
import RadioSlider from "common/components/RadioSlider";
import Select from "common/components/Select";
import Table from "common/components/Table/table";
import Menu from "common/components/Menu";
import UserInput from "common/components/UserInput";
import { Brain } from "lucide-react";
import Widget from "common/components/Widget";
import Window from "common/components/Window";
import useFetcher from "common/hooks/useFetcher";
import { createGridDashboardArray } from "common/utils/createArrayToAddUpToNumber";
import { nanoid } from "nanoid";
import CtaButton from "common/components/CtaButton";
import { CodeViewer } from "pages/Package/components/CodeGenerator";
import React, { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./builder.css";

const CONFIG = {
  commands: [
    {
      code:
        "import e1\nimport aws\n\ndef handler(context):\n    dataset = []\n    try:\n        config = e1.platform.session()\n        tenant_id = config.get('id')\n        role = config.get('role')\n        session = aws.assume({'id': tenant_id, 'role': role})\n        ec2_client = session.client('ec2', region_name=config.get('geo'))\n        response = ec2_client.describe_volumes()\n        for volume in response['Volumes']:\n            dataset.append({\n                'id': volume['VolumeId'],\n                'volume_type': volume['VolumeType'],\n                'size': volume['Size'],\n                'status': volume['State'],\n                'create_time': volume['CreateTime'].strftime('%Y-%m-%dT%H:%M:%S%z'),\n                'availability_zone': volume['AvailabilityZone'],\n                'encrypted': volume['Encrypted'],\n                'tenant_id': tenant_id,\n                'region': config.get('geo')\n            })\n    except Exception as e:\n        print(e)\n    return dataset",
      description:
        "Collects all existing EBS volumes and their details including volume ID, volume type, size, status, create time, availability zone, encryption status, tenant ID, and region.",
      name: "Collect EBS Volumes",
      type: "collector_command"
    },
    {
      code:
        "import e1\nimport aws\n\ndef handler(context):\n    deleted_volumes = []\n    for volume in context.dataset:\n        if not context.is_selected(volume):\n            continue\n        tenant_config = e1.platform.assume(volume.get('tenant_id'), '')\n        session = aws.assume(tenant_config)\n        ec2_client = session.client('ec2', region_name=volume.get('region'))\n        try:\n            ec2_client.delete_volume(VolumeId=volume.get('id'))\n            deleted_volumes.append(volume)\n        except Exception as e:\n            print(f'Failed to delete volume {volume.get('id')}: {e}')\n    context.dataset = [volume for volume in context.dataset if volume not in deleted_volumes]\n    return context.dataset",
      description:
        "Deletes the selected EBS volume(s) and removes them from the system's dataset.",
      name: "Delete EBS Volume",
      type: "resource_command"
    }
  ],
  dataset: [
    {
      availability_zone: "us-west-2a",
      create_time: "2023-01-01T00:00:00Z",
      encrypted: true,
      id: "1",
      region: "us-west-2",
      size: 10,
      status: "in-use",
      tenant_id: "tenant1",
      volume_type: "gp2"
    },
    {
      availability_zone: "us-west-2b",
      create_time: "2023-01-02T00:00:00Z",
      encrypted: false,
      id: "2",
      region: "us-west-2",
      size: 20,
      status: "available",
      tenant_id: "tenant2",
      volume_type: "io1"
    },
    {
      availability_zone: "us-west-2c",
      create_time: "2023-01-03T00:00:00Z",
      encrypted: true,
      id: "3",
      region: "us-west-2",
      size: 5,
      status: "in-use",
      tenant_id: "tenant3",
      volume_type: "gp2"
    },
    {
      availability_zone: "us-west-2d",
      create_time: "2023-01-04T00:00:00Z",
      encrypted: false,
      id: "4",
      region: "us-west-2",
      size: 30,
      status: "available",
      tenant_id: "tenant4",
      volume_type: "io1"
    },
    {
      availability_zone: "us-west-2e",
      create_time: "2023-01-05T00:00:00Z",
      encrypted: true,
      id: "5",
      region: "us-west-2",
      size: 15,
      status: "in-use",
      tenant_id: "tenant5",
      volume_type: "gp2"
    },
    {
      availability_zone: "us-west-2f",
      create_time: "2023-01-06T00:00:00Z",
      encrypted: false,
      id: "6",
      region: "us-west-2",
      size: 40,
      status: "available",
      tenant_id: "tenant6",
      volume_type: "io1"
    },
    {
      availability_zone: "us-west-2g",
      create_time: "2023-01-07T00:00:00Z",
      encrypted: true,
      id: "7",
      region: "us-west-2",
      size: 25,
      status: "in-use",
      tenant_id: "tenant7",
      volume_type: "gp2"
    },
    {
      availability_zone: "us-west-2h",
      create_time: "2023-01-08T00:00:00Z",
      encrypted: false,
      id: "8",
      region: "us-west-2",
      size: 50,
      status: "available",
      tenant_id: "tenant8",
      volume_type: "io1"
    },
    {
      availability_zone: "us-west-2i",
      create_time: "2023-01-09T00:00:00Z",
      encrypted: true,
      id: "9",
      region: "us-west-2",
      size: 10,
      status: "in-use",
      tenant_id: "tenant9",
      volume_type: "gp2"
    },
    {
      availability_zone: "us-west-2j",
      create_time: "2023-01-10T00:00:00Z",
      encrypted: false,
      id: "10",
      region: "us-west-2",
      size: 20,
      status: "available",
      tenant_id: "tenant10",
      volume_type: "io1"
    }
  ],
  description: "Collects and manages AWS EBS volumes.",
  external_packages: "",
  internal_packages: "aws",
  name: "EBS Manager",
  package_docs:
    "# aws package\n\n\n## assume\n\n    Assumes a specified IAM role in a given AWS account. Pass in the whole session generated off of\n    e1.platform.assume or e1.platform.session\n    Parameters\n    ----------\n    session : dict\n        A dictionary containing the AWS account ID and the role name to assume.\n        Expected keys: 'id' for the account ID and 'role' for the role name.\n    Returns\n    -------\n    boto3.Session\n        A new boto3 session with the assumed role's credentials.\n    Notes\n    -----\n    This function constructs an IAM role ARN from the provided account ID and role name,\n    then uses the `assume_role` function from the `aws_assume_role_lib` library to assume\n    the role. It returns a new boto3 session initialized with the credentials of the assumed role.\n    Any client that is created from the returned session should use e1.tenant().geo as the region_name if not a global client.\n    Example\n    -------\n    ```\n    config = e1.platform.assume(resource.get('tenant_id'), '')\n    session = assume(config)\n    ec2_client = session.client(\"ec2\")\n    ```",
  packages: [
    {
      id: "TyljIqqExVQj4sAxeXewI",
      pkg: "aws",
      version: "8"
    }
  ],
  platform: "amazon web services",
  resource_template:
    "### $id Volume\n\n- **Volume Type**: $volume_type\n- **Size**: $size GiB\n- **Status**: $status\n- **Created**: $create_time\n- **Availability Zone**: $availability_zone\n- **Encrypted**: $encrypted\n- **Tenant ID**: $tenant_id\n- **Region**: $region",
  schema: [
    {
      name: "id",
      type: "str",
      uid: true
    },
    {
      name: "volume_type",
      type: "str",
      uid: false
    },
    {
      name: "size",
      type: "int",
      uid: false
    },
    {
      name: "status",
      type: "str",
      uid: false
    },
    {
      name: "create_time",
      type: "str",
      uid: false
    },
    {
      name: "availability_zone",
      type: "str",
      uid: false
    },
    {
      name: "encrypted",
      type: "bool",
      uid: false
    },
    {
      name: "tenant_id",
      type: "str",
      uid: false
    },
    {
      name: "region",
      type: "str",
      uid: false
    }
  ],
  user_prompt:
    "build an EBS management system that collects my EBS volumes. Create a collector command that actually fetches all my existing EBS volumes. Resource command that deletes collected resources.",
  widgets: [
    {
      expression: "Count('id')",
      id: "7oPOBwhHQuCxxDLVeRysw",
      name: "Total EBS Volumes",
      type: "Metric",
      value: 10
    },
    {
      expression: "Sum('size')",
      group: "volume_type",
      id: "aYFyx1o8Q1SufrEjDQwFjQ",
      name: "Total Size of EBS Volumes",
      type: "Metric",
      value: 225.0
    },
    {
      expression: "Count('id')",
      group: "status",
      id: "6bb1rYhxTCacL2WdQ5wCA",
      name: "EBS Volumes by Status",
      type: "Bar",
      value: [
        ["status", null],
        ["available", 5.0],
        ["in-use", 5.0]
      ]
    }
  ]
};

const CustomHeader = params => {
  return (
    <div className="custom-header-wrapper" style={{ width: "100%" }}>
      <div
        className="ag-cell-label-container"
        role="presentation"
        style={{ display: "flex", alignItems: "center" }}
      >
        <button
          className="prompt-button"
          style={{ marginLeft: "5px", border: "none" }}
          onClick={() => {
            params.action();
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: "1rem !important" }}>
            delete
          </Icon>
        </button>
        <span
          ref={params.menuIcon}
          className="ag-header-icon ag-header-cell-menu-button"
          aria-hidden="true"
        ></span>
        <span
          ref={params.filterIcon}
          className="ag-header-icon ag-header-cell-filter-button"
          aria-hidden="true"
        ></span>
        <div
          ref={params.label}
          className="ag-header-cell-label"
          role="presentation"
        >
          <span ref={params.text} className="ag-header-cell-text">
            {params.displayName}
          </span>
          <span
            ref={params.sort}
            className="ag-header-icon ag-header-label-icon ag-sort-none-icon"
            aria-hidden="true"
          ></span>
        </div>
      </div>
      <div style={{ fontSize: ".8rem" }}>({params?.name})</div>
    </div>
  );
};

// Generate random stars function
const generateStars = count => {
  return Array.from({ length: count }, () => {
    const x = Math.floor(Math.random() * 2000);
    const y = Math.floor(Math.random() * 2000);
    return `${x}px ${y}px #FFF`;
  }).join(", ");
};

// React component

const StarBackground = () => {
  const starsRef = useRef(null);
  const stars2Ref = useRef(null);
  const stars3Ref = useRef(null);

  useEffect(() => {
    if (!starsRef.current || !stars2Ref.current || !stars3Ref.current) return;

    // Set initial box shadows
    starsRef.current.style.boxShadow = generateStars(700);
    stars2Ref.current.style.boxShadow = generateStars(200);
    stars3Ref.current.style.boxShadow = generateStars(100);
  }, []);

  return (
    <div className="star-layers">
      <div ref={starsRef} className="star-layer stars" />
      <div ref={stars2Ref} className="star-layer stars2" />
      <div ref={stars3Ref} className="star-layer stars3" />
    </div>
  );
};

const addHeaderLine = text =>
  `\n\n${text}\n${Array.from(Array(Math.min(20, text?.length)))
    .map(() => "-")
    .join("")}\n`;

export default function SystemBuilder({ role, crew_name }) {
  let refresh, loading;
  let [config, setConfig] = React.useState({}); //{};
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(); // Replace 'param' with your actual parameter name
  const system = searchParams.get("system");
  let [query, setQuery] = React.useState("");
  let [pypiPackage, setPypiPackage] = React.useState("");
  let [platform, setPlatform] = React.useState("");
  let [platforms, setPlatforms] = React.useState([]);
  let [code, setCode] = React.useState(null);
  let [urls, setUrls] = React.useState("");
  let [availableE1Pkgs, setAvailableE1Pkgs] = React.useState("");
  let [packageType, setPackageType] = React.useState(0);
  const inputRef = useRef(null);
  let [packages, setPackages] = React.useState([]);
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const fetchPkgs = async () => {
    await fetcher.get(
      "fetching platforms",
      `/api/platforms`,
      ({ platforms }) => {
        setPlatforms(platforms);
      },
      () => {}
    );
    await fetcher.get(
      "fetching packages",
      `/api/pkg`,
      ({ packages }) => {
        setAvailableE1Pkgs(packages);
      },
      () => {}
    );
  };
  function scrollToAndFocus() {
    // if (!elementRef?.current) return;
    const elementRef = document.querySelector("#echo-agent textarea");
    elementRef.scrollTop = elementRef.scrollHeight + 100;
    setTimeout(() => {
      elementRef.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
      setTimeout(() => {
        elementRef.blur();
        setTimeout(() => {
          elementRef.focus({ preventScroll: true });
          elementRef.setSelectionRange(
            elementRef.value.length,
            elementRef.value.length
          );
        }, 200);
      }, 200);
    }, 200);
  }
  const updatePrompt = (prompt, open = true) => {
    if (!open) {
      toast.success("Added to Prompt");
    }
    setQuery(q => q + "\n" + `${prompt}`);
    window.dispatchEvent(
      new CustomEvent("echo-agent-event", { detail: { open } })
    );
    document;
    scrollToAndFocus();
  };
  const loadSystem = async system => {
    const command = searchParams.get("command");
    await fetcher.get(
      "Fetching System",
      `/api/ai/system/load/${system}`,
      ({ error, config }) => {
        if (error) {
          toast.error(error);
          return;
        }
        setQuery("");
        setConfig(config);
        setPackages(config?.packages || []);
        let command_name = null;
        if (command) {
          command_name = config?.commands?.find(x => x.id === command)?.name;
        }
        if (command_name) {
          updatePrompt(`Modify '${command_name}' command: \n`);
        }
        if (!command_name) {
          command_name = config?.commands[0]?.name;
        }
        setCode(command_name);
        if (command) {
          // scroll to code el
          try {
            const element = document.getElementById(`cmd-${command}`);
            setTimeout(() => element.scrollIntoView({ block: "center" }), 300);
          } catch {}
        }
        fetchPkgs();
      },
      ({ error }) => {}
    );
  };
  useEffect(() => {
    const system = searchParams.get("system");
    if (system) {
      loadSystem(system);
    } else {
      fetchPkgs();
    }
    // if (system) {
    //   // Parameter exists, perform your action here
    //   console.log("Parameter found:", system);
    //   // Add your logic here
    // } else {
    //   // Handle case when parameter doesn't exist
    //   // console.log('No parameter provided');
    // }
  }, [location]); // Re-run effect when param changes
  // useEffect(() => {

  // }, []);

  const deploySystem = async () => {
    const system = searchParams.get("system");
    await fetcher.post(
      "Deploying System",
      `/api/ai/system/deploy`,
      { config, system },
      ({ error, id }) => {
        if (error) {
          toast.error(error);
          return;
        }
        navigate(`/app/sys/${id}/dash`);
      },
      ({ error }) => {}
    );
  };
  const dashboardGrid = createGridDashboardArray(config?.widgets?.length ?? 0);
  const getBackPage = () => {
    let system = searchParams.get("system");
    let systemName = searchParams.get("system_name");
    let command = searchParams.get("command");
    if (system && command && systemName) {
      return `/app/sys/${system}/action/${command}?system=${systemName}`;
    }
    if (system) {
      return `/app/sys/${system}/dash`;
    }
    return null;
  };
  return (
    <Page
      back={getBackPage()}
      refresh={refresh}
      header={
        config?.commands ? (
          <Stack alignItems={"center"} direction="row" spacing={1}>
            SYSTEM BUILDER
          </Stack>
        ) : null
      }
      loading={loading}
      actions={
        config?.commands
          ? [
              <ActionButton
                icon={"error"}
                noload
                danger
                dark
                handler={async () => {
                  setConfig({});
                  setQuery("");
                  setPlatform(null);
                  setUrls("");
                  setPackages([]);
                  setCode(null);
                  window.dispatchEvent(
                    new CustomEvent("echo-agent-event", {
                      detail: { open: true }
                    })
                  );
                }}
              >
                Reset Builder
              </ActionButton>,
              <ActionButton
                icon={<img height="24px" width="24px" src={AiIcon} />}
                sx={{ backgroundColor: "#a827fa" }}
                noload
                handler={deploySystem}
              >
                Deploy System
              </ActionButton>
            ]
          : null
      }
    >
      <div className="system-builder-canvas">
        <Stack>
          {config?.commands ? (
            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <UserInput
                  style={{ flex: 1, maxWidth: "350px" }}
                  label="Name"
                  icon="badge"
                  placeholder=""
                  value={config?.name}
                  onChange={e => {
                    setConfig(config => {
                      return { ...config, name: e.target.value };
                    });
                  }}
                />
                <UserInput
                  style={{ flex: 1 }}
                  label="Description"
                  icon="subtitles"
                  placeholder=""
                  value={config?.description}
                  onChange={e => {
                    setConfig(config => {
                      return { ...config, description: e.target.value };
                    });
                  }}
                />
              </Stack>

              <Stack
                sx={{ width: "100%" }}
                gap={2}
                display={"flex"}
                justifyContent={"center"}
              >
                <Window
                  title={"Widgets"}
                  icon={"bar_chart"}
                  options={[
                    {
                      icon: "add",
                      menu: true,
                      children: close => (
                        <Window
                          contentStyles={{
                            padding: 0
                          }}
                          title={"Add Widget"}
                          icon={"add_chart"}
                        >
                          <Stack
                            sx={{ width: "300px", marginTop: "10px" }}
                            gap={1}
                          >
                            {["Bar", "Pie", "Metric"].map((type, index) => {
                              return (
                                <Box
                                  sx={{
                                    padding: 0,
                                    padding: "2px 10px 2px 10px",
                                    borderBottom: "1px solid grey",
                                    cursor: "pointer",
                                    "&:hover": {
                                      background: "rgba(255, 255, 255, 0.3)"
                                    }
                                  }}
                                  onClick={() => {
                                    updatePrompt(`Add new ${type} Widget: \n`);
                                    close();
                                  }}
                                  key={type}
                                >
                                  {++index} | {type} Widget
                                </Box>
                              );
                            })}
                          </Stack>
                        </Window>
                      )
                    }
                  ]}
                >
                  <Stack
                    alignItems={"center"}
                    justifyContent={"center"}
                    direction="row"
                    gap={1}
                    marginBottom={2}
                  ></Stack>
                  <Grid
                    sx={{
                      position: "relative"
                    }}
                    container
                    spacing={1}
                  >
                    {config.widgets.map((g, i) => {
                      let index;
                      if (dashboardGrid?.length <= 2) {
                        index = 6;
                      } else {
                        index = dashboardGrid[i];
                      }
                      return (
                        <Grid
                          sx={{
                            position: "relative"
                          }}
                          key={i}
                          item
                          xs={12}
                          md={index}
                        >
                          <Widget
                            windowOptions={[
                              {
                                icon: "edit",
                                onClick: (e, { toggleWindow }) => {
                                  updatePrompt(`Modify '${g.name}' widget: \n`);
                                }
                              },
                              {
                                icon: "delete",
                                onClick: (e, { toggleWindow }) => {
                                  updatePrompt(
                                    `Delete '${g.name}' widget.\n`,
                                    false
                                  );
                                }
                              }
                            ]}
                            {...g}
                            data={g?.value}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Window>
                <Window
                  options={[
                    {
                      icon: "add",
                      onClick: (e, { toggleWindow }) => {
                        updatePrompt(`Add new property: \n`);
                      }
                    }
                  ]}
                  title={"Properties"}
                  icon={"dataset"}
                >
                  <Table
                    data={config?.dataset || []}
                    idProperty={config.schema.find(x => x.uid === "true")?.name}
                    columns={
                      config?.schema?.map(s => {
                        return {
                          headerName: s?.name,
                          field: s?.name,
                          sortable: false,
                          flex: 1,
                          minWidth: 200,
                          headerComponent: CustomHeader,
                          headerComponentParams: {
                            name: s?.name,
                            action: () =>
                              updatePrompt(
                                `Delete '${s.name}' property. \n`,
                                false
                              )
                          }
                        };
                      }) || []
                    }
                    noSelect
                    contextMenu={null}
                    noPaginate
                    loading={false}
                    tableName={"e1builderdashtable"}
                    tableHeight={400}
                  />
                </Window>
                <Window
                  options={[
                    {
                      icon: "add",
                      menu: true,
                      children: close => (
                        <Window
                          contentStyles={{
                            padding: 0
                          }}
                          title={"Add Command"}
                          icon={"keyboard_command_key"}
                        >
                          <Stack
                            sx={{ width: "300px", marginTop: "10px" }}
                            gap={1}
                          >
                            {["Collector", "Resource", "Dataset", "Read"].map(
                              (type, index) => {
                                return (
                                  <Box
                                    sx={{
                                      padding: 0,
                                      padding: "2px 10px 2px 10px",
                                      borderBottom: "1px solid grey",
                                      cursor: "pointer",
                                      "&:hover": {
                                        background: "rgba(255, 255, 255, 0.3)"
                                      }
                                    }}
                                    onClick={() => {
                                      updatePrompt(
                                        `Add new ${type} Command: \n`
                                      );
                                      close();
                                    }}
                                    key={type}
                                  >
                                    {++index} | {type} Command
                                  </Box>
                                );
                              }
                            )}
                          </Stack>
                        </Window>
                      )
                    }
                  ]}
                  title={"Command Palette"}
                  icon={"bolt"}
                >
                  <Stack
                    p={2}
                    sx={{ border: "1px solid #eee", borderRadius: "8px" }}
                    direction="row"
                    gap={1}
                  >
                    <Window
                      icon="keyboard_command_key"
                      sx={{ width: "300px" }}
                      title={"Commands"}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px"
                        }}
                      >
                        {config.commands.map((a, i) => {
                          return (
                            <div
                              id={`cmd-${a?.id}`}
                              style={{
                                backgroundImage: `url(${Background})`,
                                backgroundSize: "cover",
                                backgroundRepeat: "no-repeat",
                                background: `
                                  linear-gradient(
                                    rgba(58, 14, 81, ${
                                      a.name === code ? 0.5 : 0
                                    }),
                                    rgba(58, 14, 81, ${
                                      a.name === code ? 0.1 : 0
                                    })
                                  ),
                                  url(${Background})
                                `
                              }}
                              key={i}
                              className="floating"
                            >
                              <Stack
                                alignItems={"center"}
                                direction="row"
                                gap={1}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    letterSpacing: "1.1px"
                                  }}
                                  variant="h5"
                                >
                                  {a.name}
                                </Typography>
                              </Stack>
                              <Stack gap={1}>
                                <Typography>{a.description}</Typography>
                              </Stack>

                              <div
                                style={{
                                  marginTop: "10px",
                                  marginLeft: "auto",
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  gap: "10px",
                                  borderRadius: "12px",
                                  maxWidth: "140px",
                                  background: "rgba(0,0,0,.5)"
                                }}
                              >
                                <IconButton
                                  onClick={() => {
                                    setCode(a.name || "");
                                  }}
                                  sx={{
                                    "&:hover": {
                                      background: "#a827fa"
                                    }
                                  }}
                                >
                                  <Icon sx={{ color: "#fff !important" }}>
                                    search
                                  </Icon>
                                </IconButton>
                                <IconButton
                                  onClick={() => {
                                    updatePrompt(
                                      `Modify '${a.name}' command: \n`
                                    );
                                  }}
                                  sx={{
                                    "&:hover": {
                                      background: "#a827fa"
                                    }
                                  }}
                                >
                                  <Icon sx={{ color: "#fff !important" }}>
                                    edit
                                  </Icon>
                                </IconButton>
                                <IconButton
                                  onClick={() => {
                                    updatePrompt(
                                      `Delete '${a.name}' command. \n`,
                                      false
                                    );
                                  }}
                                  sx={{
                                    "&:hover": {
                                      background: "#a827fa"
                                    }
                                  }}
                                >
                                  <Icon sx={{ color: "#fff !important" }}>
                                    delete
                                  </Icon>
                                </IconButton>
                                {/* <ActionButton
                                  icon={a.name === code ? "close" : "search"}
                                  dark
                                  noload
                                  sx={{
                                    zIndex: 10
                                  }}
                                  handler={async () => {
                                    if (a.name === code) return setCode(null);
                                    setCode(a.name);
                                  }}
                                >
                                  {a.name === code ? "Close" : "Inspect"}
                                </ActionButton>*/}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Window>

                    {code ? (
                      <Stack
                        flex={1}
                        direction="column"
                        gap={2}
                        marginBottom={2}
                      >
                        <Window icon="data_array" title={"Command Inputs"}>
                          <Table
                            overlayNoRowsTemplate={
                              "<span>No command inputs</span>"
                            }
                            data={
                              config?.commands?.find(x => x.name === code)
                                ?.inputs || []
                            }
                            idProperty={"name"}
                            columns={[
                              {
                                headerName: "name",
                                field: "name",
                                sortable: false,
                                minWidth: 200,
                                flex: 1
                              },
                              {
                                headerName: "type",
                                field: "type",
                                sortable: false,
                                minWidth: 200,
                                flex: 1
                              },
                              {
                                headerName: "",
                                field: "options",
                                sortable: false,
                                minWidth: 200,
                                flex: 1,
                                cellRenderer: ({ data }) => {
                                  return null;
                                  // <button
                                  //   onClick={() => {
                                  //     setQuery(
                                  //       q =>
                                  //         q +
                                  //         `\n\nDelete '${code}' command's ${data?.name} input`
                                  //     );
                                  //   }}
                                  //   className="prompt-button"
                                  // >
                                  //   Remove
                                  // </button>
                                }
                              }
                            ]}
                            noSelect
                            noPaginate
                            loading={false}
                            tableName={"e1buildercommandinputstable"}
                            tableHeight={200}
                          />
                        </Window>
                        <Window title={"Command Code"} icon={"code"}>
                          <div style={{ position: "relative" }}>
                            <CodeViewer
                              code={
                                config?.commands?.find(x => x.name === code)
                                  ?.code || ""
                              }
                              language={"python"}
                            />
                          </div>
                        </Window>
                      </Stack>
                    ) : (
                      <Stack
                        flex={1}
                        direction="column"
                        gap={2}
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        marginBottom={2}
                      >
                        <Typography
                          variant="overline"
                          sx={{
                            fontWeight: "bold",
                            display: "flex",
                            gap: "5px",
                            alignItems: "center",
                            fontSize: "1rem"
                          }}
                        >
                          <Icon sx={{ color: "#fff" }}>arrow_back_2</Icon>{" "}
                          Select a command to review code
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Window>
              </Stack>
            </Stack>
          ) : (
            <>
              <StarBackground />
              <Stack
                sx={{ height: "80vh", width: "100vw" }}
                alignItems={"center"}
                justifyContent={"center"}
              >
                {/* <AiAvatar />*/}
              </Stack>
            </>
          )}
        </Stack>
        <Window
          options={[
            {
              icon: open => (!open ? "open_in_full" : "minimize"),
              onClick: (e, { toggleWindow }) => {
                toggleWindow();
              }
            }
          ]}
          sx={open => ({
            height: "85%",
            width: "500px",
            maxHeight: "700px",
            position: "fixed",
            right: "20px",
            top: open ? "80px" : "180px"
          })}
          title={"Echo Agent"}
          icon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-bot-icon lucide-bot"
            >
              <path d="M12 8V4H8" />
              <rect width="16" height="12" x="4" y="8" rx="2" />
              <path d="M2 14h2" />
              <path d="M20 14h2" />
              <path d="M15 13v2" />
              <path d="M9 13v2" />
            </svg>
          }
          id="echo-agent"
        >
          {({ toggleWindow }) => (
            <Stack sx={{ height: "100%" }} gap="10px">
              <div style={{ position: "relative" }}>
                <textarea
                  className="ai-textarea"
                  tabIndex={-1}
                  style={{ flex: 1 }}
                  placeholder={
                    config?.commands?.length
                      ? "To modify this system, provide detailed descriptions of modifications you would like to apply. Make sure to be as specific as possible and use the prompt helper buttons to help articulate intent.\n\nThe more specific you are, the better the output"
                      : "To get started, provide a detailed description of the System that you would like to create. Make sure to be specific about the dataset properties and commands you'd like generated.\n\nThe more specific you are, the better the output"
                  }
                  ref={inputRef}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                <CtaButton
                  style={{
                    position: "absolute",
                    bottom: "15px",
                    right: "10px"
                  }}
                  onClick={async () => {
                    if (!query?.trim())
                      return toast.error("enter a query first");
                    await fetcher.post(
                      "Optimizing prompt",
                      `/api/ai/prompt/optimize`,
                      {
                        query
                      },
                      ({ error, optimized }) => {
                        if (error) {
                          toast.error(error);
                          return;
                        }
                        if (optimized) {
                          setQuery(optimized.replace(/\\n/g, "\n"));
                        }
                      },
                      ({ error }) => {}
                    );
                  }}
                  size={30}
                  tip="Optimize"
                >
                  sparkles
                </CtaButton>
              </div>
              <Window
                sx={{
                  height: "200px",
                  width: "100%"
                }}
                subWindow
                title={"Packages"}
                icon={"token"}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px"
                  }}
                >
                  <RadioSlider
                    options={["E1 Package", "PYPI"]}
                    value={packageType}
                    onChange={setPackageType}
                  />
                  {!packageType ? (
                    <Select
                      label={"package"}
                      onSelect={value =>
                        setPackages([...packages, { id: nanoid(), ...value }])
                      }
                      options={{
                        dataset: availableE1Pkgs || [],
                        selector: (current, dataset) => {
                          if (!current?.pkg) {
                            return [
                              dataset,
                              "pk",
                              "name",
                              "Select a Echelon One Package"
                            ];
                          } else if (!current?.version) {
                            let pkg = dataset.find(d => d?.pk === current?.pkg);
                            return [
                              pkg?.versions?.map(v => ({ value: v, label: v })),
                              "value",
                              "label",
                              "Select a Version"
                            ];
                          } else {
                            return null;
                          }
                        },
                        pick: (current, selected, set, onSelect) => {
                          if (!current?.pkg) {
                            set({ ...current, pkg: selected?.pk });
                          } else if (!current?.version) {
                            set({ ...current, version: selected?.value });
                          } else {
                            return null;
                          }
                        },
                        label: current => {
                          return (
                            current.pkg +
                            (current?.version ? ` (v${current.version})` : "")
                          );
                        }
                      }}
                    />
                  ) : (
                    <UserInput
                      label="PyPI"
                      placeholder="requests==2.32.5"
                      value={pypiPackage}
                      onChange={e => setPypiPackage(e.target.value)}
                      onAdd={({ value }) => {
                        setPackages([
                          ...packages,
                          { id: nanoid(), pkg: value, pypi: true }
                        ]);
                        setPypiPackage("");
                      }}
                    />
                  )}
                  <Chips
                    items={packages}
                    labelKey={value =>
                      value.pypi ? value.pkg : `${value.pkg}(${value.version})`
                    }
                    options={[
                      {
                        icon: "delete",
                        onClick: item => {
                          setPackages(items =>
                            items.filter(i => i.id !== item.id)
                          );
                        }
                      }
                    ]}
                  />
                </div>
              </Window>
              <Window
                subWindow
                sx={{
                  height: "100px",
                  width: "100%"
                }}
                title={"Platform"}
                icon={"apps"}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px"
                  }}
                >
                  <Select
                    noAdd
                    label={"platform"}
                    value={platform?.pk || ""}
                    onSelect={value => setPlatform(value)}
                    options={{
                      dataset: platforms || [],
                      selector: (current, dataset) => {
                        return [dataset, "pk", "name", "Select a Platform"];
                      },
                      pick: (current, selected, set, onSelect) => {
                        set({ ...current, platform: selected?.pk });
                        onSelect(selected);
                      },
                      label: current => {
                        return current.platform;
                      }
                    }}
                  />
                </div>
              </Window>
              <button
                style={{ maxHeight: "60px" }}
                className="e1-btn"
                onClick={async () => {
                  if (!query?.trim()) return toast.error("enter a query first");
                  toggleWindow(false);
                  // setCode(null);
                  await fetcher.post(
                    "Generating system",
                    `/api/ai/system/builder`,
                    {
                      packages,
                      urls,
                      query,
                      config,
                      platform: platform?.pk
                      // conversation: convo
                    },
                    ({ error, config }) => {
                      if (error) {
                        toast.error(error);
                        return;
                      }
                      setQuery("");
                      setConfig(config);
                      if (!code) {
                        setCode(config?.commands[0]?.name || null);
                      }
                      // setConvo(conversation);
                    },
                    ({ error }) => {}
                  );
                }}
              >
                GENERATE
              </button>
            </Stack>
          )}
        </Window>
      </div>
      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 2, md: 3 }}
        sx={{ height: "100%" }}
      ></Stack>
      <div style={{ height: "200px" }} />
    </Page>
  );
}
